import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/AuthProvider';
import { DataService, Organization } from './services/DataService';
import { 
  ArrowLeft,
  Plus,
  Trash2,
  Eye,
  Edit,
  Shield,
  Check,
  Users,
  UserPlus,
  UserMinus
} from 'lucide-react';

interface OrganizationPermissions {
  read?: string[];
  write?: string[];
  admin?: string[];
  api?: string[];
}

interface UserWithPermissions {
  email: string;
  organizationRoles: {
    read: boolean;
    write: boolean;
    admin: boolean;
    api: boolean;
  };
}

interface PermissionsManagementProps {
  organizationId: string;
  onBack: () => void;
}

const PermissionsManagement: React.FC<PermissionsManagementProps> = ({
  organizationId,
  onBack
}) => {
  const { currentUser } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizationPermissions, setOrganizationPermissions] = useState<OrganizationPermissions>({});
  const [usersWithPermissions, setUsersWithPermissions] = useState<UserWithPermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRoles, setNewUserRoles] = useState<{[key: string]: boolean}>({
    read: false,
    write: false,
    admin: false,
    api: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<{
    email: string;
    roles: {read: boolean, write: boolean, admin: boolean, api: boolean};
  } | null>(null);

  useEffect(() => {
    loadOrganizationData();
  }, [organizationId]);

  // Auto-dismiss messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const loadOrganizationData = async () => {
    try {
      setLoading(true);
      
      // Load organization details
      const org = await DataService.getOrganization(organizationId);
      setOrganization(org);

      // Load organization permissions
      const permissions = await DataService.getOrganizationPermissions(organizationId);
      if (permissions) {
        setOrganizationPermissions(permissions);

        // Get all unique user emails from permissions
        const allUserEmails = new Set<string>();
        
        Object.values(permissions).forEach((userList) => {
          if (Array.isArray(userList)) {
            userList.forEach((email) => allUserEmails.add(email));
          }
        });

        // Create user objects with their roles
        const userResults: UserWithPermissions[] = Array.from(allUserEmails).map((email) => ({
          email,
          organizationRoles: {
            read: permissions.read?.includes(email) || false,
            write: permissions.write?.includes(email) || false,
            admin: permissions.admin?.includes(email) || false,
            api: permissions.api?.includes(email) || false,
          }
        }));
        
        // Sort users: current user first, then alphabetically
        userResults.sort((a, b) => {
          if (a.email === currentUser?.email) return -1;
          if (b.email === currentUser?.email) return 1;
          return a.email.localeCompare(b.email);
        });
        
        setUsersWithPermissions(userResults);
      }

    } catch (err: any) {
      console.error('Error loading organization data:', err);
      setError(err.message || 'Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = async (userEmail: string, role: 'read' | 'write' | 'admin' | 'api', currentlyHasRole: boolean) => {
    if (userEmail === currentUser?.email) {
      setError('Cannot modify your own permissions');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const action = currentlyHasRole ? 'remove' : 'add';
      
      await DataService.setOrganizationPermission(organizationId, role, userEmail, action);
      
      setSuccess(`${action === 'add' ? 'Added' : 'Removed'} ${role} permission for ${userEmail}`);
      
      // Reload data to reflect changes
      await loadOrganizationData();
    } catch (err: any) {
      console.error('Error updating user permissions:', err);
      setError(err.message || 'Failed to update user permissions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    if (!newUserEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    const selectedRoles = Object.entries(newUserRoles).filter(([_, selected]) => selected);
    if (selectedRoles.length === 0) {
      setError('Please select at least one role for the user');
      return;
    }

    setIsSubmitting(true);

    try {
      // Add each selected role
      for (const [role, _] of selectedRoles) {
        await DataService.setOrganizationPermission(
          organizationId, 
          role as 'read' | 'write' | 'admin' | 'api', 
          newUserEmail.trim(), 
          'add'
        );
      }

      setSuccess(`User ${newUserEmail} added successfully with ${selectedRoles.length} role${selectedRoles.length > 1 ? 's' : ''}`);
      
      // Reset form
      setNewUserEmail('');
      setNewUserRoles({ read: false, write: false, admin: false, api: false });
      setShowAddUserForm(false);
      
      // Reload data
      await loadOrganizationData();

    } catch (err: any) {
      console.error('Error adding user:', err);
      setError(err.message || 'Failed to add user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveUser = async (userEmail: string, userRoles: {read: boolean, write: boolean, admin: boolean, api: boolean}) => {
    if (userEmail === currentUser?.email) {
      setError('Cannot remove your own permissions');
      return;
    }

    setDeleteConfirmUser({ email: userEmail, roles: userRoles });
  };

  const confirmRemoveUser = async () => {
    if (!deleteConfirmUser) return;

    setIsSubmitting(true);
    
    try {
      // Remove all roles for this user
      const rolesToRemove = Object.entries(deleteConfirmUser.roles).filter(([_, hasRole]) => hasRole);
      
      for (const [role, _] of rolesToRemove) {
        await DataService.setOrganizationPermission(
          organizationId,
          role as 'read' | 'write' | 'admin' | 'api',
          deleteConfirmUser.email,
          'remove'
        );
      }

      setSuccess(`All permissions removed for ${deleteConfirmUser.email}`);
      setDeleteConfirmUser(null);
      
      // Reload data
      await loadOrganizationData();

    } catch (err: any) {
      console.error('Error removing user:', err);
      setError(err.message || 'Failed to remove user permissions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleBadgeColor = (role: 'read' | 'write' | 'admin' | 'api'): string => {
    switch (role) {
      case 'read': return 'bg-green-100 text-green-800 border-green-200';
      case 'write': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'api': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleIcon = (role: 'read' | 'write' | 'admin' | 'api') => {
    switch (role) {
      case 'read': return <Eye className="w-4 h-4" />;
      case 'write': return <Edit className="w-4 h-4" />;
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'api': return <Users className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Permissions...</h2>
          <p className="text-gray-600">Please wait while we load the permission data</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Organization not found
          </div>
          <button 
            onClick={onBack}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Back to organization"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Shield className="w-6 h-6 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Organization Permissions</h1>
                  <p className="text-sm text-gray-500">{organization.name}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">
                {usersWithPermissions.length} user{usersWithPermissions.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setShowAddUserForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                disabled={isSubmitting}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button 
                onClick={() => setError('')}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-green-50 border-l-4 border-green-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
              <button 
                onClick={() => setSuccess('')}
                className="ml-auto text-green-400 hover:text-green-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {usersWithPermissions.length === 0 ? (
          <div className="text-center bg-white rounded-lg border border-gray-200 p-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
            <p className="text-gray-600 mb-6">No users with permissions found for this organization.</p>
            <button
              onClick={() => setShowAddUserForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Your First User
            </button>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[
                { role: 'read', label: 'Read Access', icon: <Eye className="w-5 h-5" /> },
                { role: 'write', label: 'Write Access', icon: <Edit className="w-5 h-5" /> },
                { role: 'admin', label: 'Admin Access', icon: <Shield className="w-5 h-5" /> },
                { role: 'api', label: 'API Access', icon: <Users className="w-5 h-5" /> }
              ].map(({ role, label, icon }) => (
                <div key={role} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${getRoleBadgeColor(role as any)} border`}>
                      {icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-900">{label}</h3>
                      <p className="text-2xl font-bold text-gray-900">
                        {usersWithPermissions.filter(u => u.organizationRoles[role as keyof typeof u.organizationRoles]).length}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Users Table */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">User Permissions</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Permissions
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usersWithPermissions.map((user) => (
                      <tr key={user.email} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {user.email}
                                {user.email === currentUser?.email && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    You
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {(['read', 'write', 'admin', 'api'] as const).map((role) => (
                              <button
                                key={role}
                                onClick={() => handleRoleToggle(user.email, role, user.organizationRoles[role])}
                                disabled={isSubmitting || user.email === currentUser?.email}
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                                  user.organizationRoles[role] 
                                    ? getRoleBadgeColor(role)
                                    : 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'
                                } ${
                                  user.email === currentUser?.email 
                                    ? 'cursor-not-allowed opacity-50' 
                                    : 'cursor-pointer hover:shadow-sm'
                                }`}
                                title={
                                  user.email === currentUser?.email 
                                    ? 'Cannot modify your own permissions' 
                                    : `Click to ${user.organizationRoles[role] ? 'remove' : 'add'} ${role} access`
                                }
                              >
                                {getRoleIcon(role)}
                                <span className="ml-1 capitalize">{role}</span>
                                {user.organizationRoles[role] && (
                                  <Check className="w-3 h-3 ml-1" />
                                )}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {user.email !== currentUser?.email && (
                            <button
                              onClick={() => handleRemoveUser(user.email, user.organizationRoles)}
                              disabled={isSubmitting}
                              className="text-red-600 hover:text-red-900 transition-colors disabled:opacity-50"
                              title="Remove all access"
                            >
                              <UserMinus className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUserForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Add User to {organization.name}</h3>
                <button 
                  onClick={() => setShowAddUserForm(false)}
                  disabled={isSubmitting}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Plus className="w-5 h-5 transform rotate-45" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Email Address
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Assign Permissions
                  </label>
                  <div className="space-y-3">
                    {[
                      { role: 'read', title: 'Read Access', description: 'Can view organization data' },
                      { role: 'write', title: 'Write Access', description: 'Can create and edit content' },
                      { role: 'admin', title: 'Admin Access', description: 'Full access including user management' },
                      { role: 'api', title: 'API Access', description: 'Can access via API endpoints' }
                    ].map(({ role, title, description }) => (
                      <label key={role} className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newUserRoles[role]}
                          onChange={(e) => setNewUserRoles(prev => ({
                            ...prev,
                            [role]: e.target.checked
                          }))}
                          disabled={isSubmitting}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{title}</div>
                          <div className="text-xs text-gray-500">{description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddUserForm(false)}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                disabled={isSubmitting || !newUserEmail.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Adding User...' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserMinus className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Remove User Access</h3>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <p className="text-sm text-gray-500 mb-3">
                Are you sure you want to remove all permissions for this user? This action cannot be undone.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800 font-medium">
                  User: {deleteConfirmUser.email}
                </p>
                <p className="text-sm text-red-600 mt-1">
                  This will remove all organization access for this user.
                </p>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmUser(null)}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveUser}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Removing...' : 'Remove User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionsManagement;